import { useState, useEffect } from "react";
import { X, Megaphone } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useInteractionReady } from "@/components/ui/InteractionGate";

const CACHE_KEY = "announcement-banner-v1";

type CachedBanner = { enabled: boolean; text?: string; link_url?: string; link_label?: string } | null;

function readCache(): CachedBanner {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedBanner;
  } catch {
    return null;
  }
}

const AnnouncementBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const interactionReady = useInteractionReady();

  // Synchronous cache so banner state is decided before first paint.
  // This prevents the banner from "dropping in" 1.5s later and appearing
  // to overlay the top of non-home pages (header is position:fixed).
  const cached = readCache();

  // Revalidate in background. We don't gate render on this — the cache
  // drives initial render so there's no late layout shift.
  const { data: batch } = useSiteSettings({ enabled: interactionReady });
  const fresh = (batch?.announcement_banner ?? null) as Record<string, any> | null;

  useEffect(() => {
    if (!fresh) return;
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          enabled: !!fresh['enabled'],
          text: fresh['text'],
          link_url: fresh['link_url'],
          link_label: fresh['link_label'],
        })
      );
    } catch {
      /* ignore quota */
    }
  }, [fresh]);

  // Prefer fresh data once it arrives, otherwise fall back to cache.
  const settings = (fresh ?? cached) as Record<string, any> | null;
  const hidden = !settings || !settings['enabled'] || !settings['text'] || dismissed;
  if (hidden) return null;

  const linkUrl = settings['link_url'] || null;
  const linkLabel = settings['link_label'] || "Read announcement details";

  return (
    <div className="relative bg-accent text-accent-foreground text-center text-xs sm:text-sm py-2 px-8 min-h-[74px] sm:min-h-[40px] flex items-center justify-center">
      <div className="container flex items-center justify-center gap-2 flex-wrap">
        <Megaphone className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">{settings['text']}</span>
        {linkUrl && (
          <a
            href={linkUrl}
            className="underline font-semibold hover:opacity-80 transition-opacity"
          >
            {linkLabel}
          </a>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-accent-foreground/10 transition-colors"
        aria-label="Dismiss announcement"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default AnnouncementBanner;
